"use client";
import Head from "next/head";
import React, { useState } from "react";
import { useTableSortQuery } from "~/components/table/useTableSortQuery.jsx";
import { fetchEntitiesPaginated, fetchEntityActions } from "~/services";
import { useTablePageState, useTableSizeState } from "~/hooks";
import { ENTITY_TYPE } from "~/constants";

import TurnrTableSmart, { TurnrTableCell, TurnrTableRow } from "~/components/table/TurnrTableSmart.jsx";
import { TurnrCard, TurnrCardContent } from "~/components/card/TurnrCard.jsx";
import { getApiKeysPageData } from "~/cores/pageAccessCore";
import PageInformationCard from "~/components/PageInformationCard.jsx";
import { useServiceHook } from "rylak-common";
import { useRunCallbackIfValueDeeplyChanged } from "rylak-common/src/hooks/useRunCallbackIfValueDeeplyChanged.jsx";
import { getTableHeadActions, TableCellActions } from "~/components/table/cells/TableCellActions.jsx";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import { getTableHeadBrand, TableCellBrand } from "~/components/table/cells/TableCellBrand.jsx";
import { TableCellId } from "~/components/table/cells/TableCellId.jsx";
import { getTableHeadDateTime, TableCellDateTime } from "~/components/table/cells/TableCellDateTime.jsx";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import EntityActionButton from "~/components/entityAction/EntityActionButton.jsx";
import { extractCreateEntityAction } from "~/components/entityAction/utils/entityActionsCore.js";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { CopyClickableIcon } from "~/components/copyText/CopyClickableIcon.jsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TurnrTooltip } from "rylak-common/src/components/TurnrTooltip/TurnrTooltip";
import { faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import TurnrModal, { TurnrModalContent } from "~/components/modals/TurnrModal.jsx";
import { PrettyCopyBox } from "~/components/copyText/PrettyCopyBox.jsx";
import LineDivider from "rylak-common/src/components/divider/LineDivider";
import { useAppProps } from "~/app/_apps/AppPropsContext";
import ExternalLink from "~/components/ExternalLink.jsx";

function getTableHeadData({ appProps }) {
    return {
        items: [
            getTableHeadBrand({ id: "apiKey.merchantBrand.name", appProps, sortable: false }),

            {
                id: "name",
                label: "Name",
                sortable: true,
            },
            {
                id: "key",
                label: "API key",
                sortable: true,
            },
            getTableHeadDateTime({ defaultSortedBy: true, hide: "lg" }),
            getTableHeadDateTime({ id: "lastUsedAt", label: "Last used", hide: "sm" }),
            {
                id: "usageCount",
                label: "Times used",
                sortable: true,
                defaultSortDirection: "desc",
                hide: "md",
            },
            getTableHeadActions(),
        ].filter((v) => v),
    };
}

export default function SettingsApiKeysPage() {
    const { appProps } = useAppProps();

    const tableHeadData = getTableHeadData({ appProps });
    const [lastCreatedApiKeyEntity, setLastCreatedApiKeyEntity] = useState(null);
    let tableSortData = useTableSortQuery(tableHeadData);
    let { sort } = tableSortData;

    const [page, setPage] = useTablePageState();
    const [size] = useTableSizeState();

    let { serviceData: apiKeysService, fetchServiceData: fetchApiKeysServiceFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntitiesPaginated,
    });

    let { serviceData: apiKeysActions, fetchServiceData: fetchApiKeysActionsFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntityActions,
    });

    function triggerApiKeysFetch() {
        fetchApiKeysServiceFn(
            {
                entityType: ENTITY_TYPE.API_KEY,
                page,
                size,
                sort: sort,
            },
            { silentFetch: true },
        );
    }

    let triggerApiKeysActionsFetch = () => {
        fetchApiKeysActionsFn(
            {
                entityType: ENTITY_TYPE.API_KEY,
            },
            { silentFetch: true },
        );
    };

    useRunCallbackIfValueDeeplyChanged([page, size, sort], () => {
        triggerApiKeysFetch();
        triggerApiKeysActionsFetch();
    });

    const [createAction, filteredActions] = extractCreateEntityAction(apiKeysActions?.data);

    const [keysToShow, setKeysToShow] = useState({});

    function onShowClick(id) {
        keysToShow[id] = true;
        setKeysToShow({ ...keysToShow });
    }

    return (
        <>
            <Head>
                <title>API Keys</title>
            </Head>
            <PageInformationCard
                iconProps={{ flip: "horizontal" }}
                icon={getApiKeysPageData().icon}
                title={getApiKeysPageData().title}
                content={
                    <div>
                        <div>Here you can create API keys to use when connecting your services to our system using our public endpoints.</div>
                        <br />
                        <ExternalLink href={"https://developer.ingrid.com/returns/public-api/endpoints"}>Public Endpoints Documentation</ExternalLink>
                        <br />
                        <ExternalLink href={"https://api.ingrid.com/returns/swagger-ui/index.html"}>Up-to-date Swagger Endpoints Documentation</ExternalLink>
                        <br />
                        Once created you have 5 min to view the key. If you ever lose it you can always come here and simply create a new one.
                    </div>
                }
            />
            <TurnrModal
                title={"Here is your API key!"}
                description={
                    <div>
                        You will be able to view this key for up to 5 min on this page. <br />
                        If you lose the key you can create a new one.
                    </div>
                }
                open={!!lastCreatedApiKeyEntity}
                onClose={() => setLastCreatedApiKeyEntity(null)}
            >
                <LineDivider />
                <TurnrModalContent>
                    <PrettyCopyBox text={lastCreatedApiKeyEntity?.key} />
                </TurnrModalContent>
            </TurnrModal>
            <TurnrCard>
                <TurnrCardContent>
                    <TurnrGrid container spacing={3} alignItems={"center"}>
                        {createAction && (
                            <TurnrGrid item>
                                <EntityActionButton
                                    appProps={appProps}
                                    entityAction={createAction}
                                    onSuccess={(createdServiceResponse) => {
                                        setLastCreatedApiKeyEntity(createdServiceResponse?.data);
                                        triggerApiKeysFetch();
                                    }}
                                    buttonProps={{
                                        variant: "outlined",
                                        size: "medium",
                                    }}
                                />
                            </TurnrGrid>
                        )}
                        <TurnrGrid item>
                            <EntityActionsDotsMenu hideIfEmpty={true} appProps={appProps} entityType={ENTITY_TYPE.API_KEY} entityActions={filteredActions} />
                        </TurnrGrid>
                    </TurnrGrid>
                </TurnrCardContent>
                <TurnrTableSmart cellSize="small" tableHeadData={tableHeadData} tableSortData={tableSortData} serviceData={apiKeysService} page={page} setPage={setPage} pageSize={size}>
                    {apiKeysService?.data?.content?.map((apiKeyEntity) => {
                        const { id } = apiKeyEntity;
                        return (
                            <TurnrTableRow key={id}>
                                <TableCellBrand entity={apiKeyEntity} appProps={appProps} />
                                <TableCellId id={apiKeyEntity?.name} tight />
                                {/*<TableCellId id={apiKeyEntity?.key} />*/}
                                <TurnrTableCell>
                                    {!apiKeyEntity?.key && (
                                        <TurnrTooltip content={"This key is now hidden. If you don't have it saved somewhere you will have to create a new API key"}>
                                            <FontAwesomeIcon icon={faEyeSlash} />
                                        </TurnrTooltip>
                                    )}
                                    {apiKeyEntity?.key && (
                                        <div>
                                            {keysToShow[id] ? (
                                                <div>
                                                    <CopyClickableIcon
                                                        textToCopy={apiKeyEntity?.key}
                                                        text={<div style={{ overflowWrap: "anywhere" }}>{apiKeyEntity?.key}</div>}
                                                        iconPos="left"
                                                        alwaysShowIcon={true}
                                                        copyHoverText={null}
                                                    />
                                                </div>
                                            ) : (
                                                <TurnrButton size={"small"} variant={"text"} onClick={() => onShowClick(id)}>
                                                    Show
                                                </TurnrButton>
                                            )}
                                        </div>
                                    )}
                                </TurnrTableCell>
                                <TableCellDateTime dateTime={apiKeyEntity?.createdAt} />
                                <TableCellDateTime dateTime={apiKeyEntity?.lastUsedAt} />
                                <TurnrTableCell>{apiKeyEntity?.usageCount}</TurnrTableCell>
                                <TableCellActions>
                                    <EntityActionsDotsMenu entity={apiKeyEntity} appProps={appProps} onSuccess={triggerApiKeysFetch} />
                                </TableCellActions>
                            </TurnrTableRow>
                        );
                    })}
                </TurnrTableSmart>
            </TurnrCard>
        </>
    );
}
